import {
  DirectionsBody, FormState, LonLat, MatrixBody, TruckRestrictions,
} from '../types';

function numOrNull(v: string): number | null {
  const t = v.trim();
  if (t === '') return null;
  const n = parseFloat(t);
  return isNaN(n) ? null : n;
}

function collectRestrictions(f: FormState): TruckRestrictions {
  const r: TruckRestrictions = {};
  const map: Array<[keyof TruckRestrictions, string]> = [
    ['length', f.restrictions.length],
    ['width',  f.restrictions.width],
    ['height', f.restrictions.height],
    ['weight', f.restrictions.weight],
    ['axleload', f.restrictions.axleload],
  ];
  for (const [k, v] of map) {
    const n = numOrNull(v);
    if (n !== null) (r as any)[k] = n;
  }
  if (f.restrictions.hazmat) r.hazmat = true;
  return r;
}

function coordsFromForm(f: FormState): LonLat[] {
  return f.waypoints
    .filter(w => w.lon !== null && w.lat !== null)
    .map(w => [w.lon as number, w.lat as number]);
}

export function buildDirectionsBody(f: FormState): DirectionsBody {
  const coords = coordsFromForm(f);
  if (coords.length < 2) throw new Error('Need at least 2 waypoints with valid lon/lat');

  const body: DirectionsBody = {
    coordinates: coords,
    preference: f.preference,
    units: f.units,
    language: f.language,
    instructions: f.instructions,
    elevation: f.elevation,
    ...(f.disableCH ? { optimized: false } : {}),
  };

  const r = f.radiuses.trim();
  if (r) {
    body.radiuses = r.split(',').map(x => {
      const n = parseFloat(x.trim());
      return isNaN(n) ? -1 : n;
    });
  }

  const extras = Object.entries(f.extras).filter(([, v]) => v).map(([k]) => k);
  if (extras.length) body.extra_info = extras;

  const attrs = Object.entries(f.attributes).filter(([, v]) => v).map(([k]) => k);
  if (attrs.length) body.attributes = attrs;

  const options: NonNullable<DirectionsBody['options']> = {};
  const avoid = Object.entries(f.avoid).filter(([, v]) => v).map(([k]) => k);
  if (avoid.length) options.avoid_features = avoid;

  if (f.profile === 'driving-hgv') {
    const restrictions = collectRestrictions(f);
    if (Object.keys(restrictions).length) {
      options.profile_params = { restrictions };
      options.vehicle_type = 'hgv';
    }
  }

  // maximum_speed
  const maxSpd = numOrNull(f.maximumSpeed ?? '');
  if (maxSpd !== null && maxSpd > 0) body.maximum_speed = maxSpd;

  // avoid_polygons — prefer drawn list (active only) over raw JSON
  const activePolygons = (f.avoidPolygons || []).filter(p => p.active !== false);
  if (activePolygons.length > 0) {
    const closeRing = (verts: [number,number][]) => [...verts, verts[0]];
    if (activePolygons.length === 1) {
      options.avoid_polygons = { type: 'Polygon', coordinates: [closeRing(activePolygons[0].vertices)] };
    } else {
      options.avoid_polygons = {
        type: 'MultiPolygon',
        coordinates: activePolygons.map(p => [closeRing(p.vertices)]),
      };
    }
  } else if (f.avoidPolygonsJson?.trim()) {
    try {
      options.avoid_polygons = JSON.parse(f.avoidPolygonsJson);
    } catch {
      throw new Error('Invalid JSON in avoid_polygons');
    }
  }

  if (Object.keys(options).length) body.options = options;

  if (body.preference === 'custom') {
    if (f.customModelOverride && f.customModelOverrideJson.trim()) {
      // Raw JSON override — send as-is
      try {
        body.custom_model = JSON.parse(f.customModelOverrideJson);
      } catch {
        throw new Error('Invalid JSON in custom model override');
      }
    } else {
      // Assemble from individual controls
      const cm: DirectionsBody['custom_model'] = {};
      const di = numOrNull(f.distInfluence);
      if (di !== null) cm.distance_influence = di;

      // Build priority array from factor rows + raw priorityJson (merged)
      const priorityFromFactors = f.cmFactors
        .filter(r => r.type === 'priority' && r.condition.trim() && r.multiplyBy.trim())
        .map(r => ({ if: r.condition.trim(), multiply_by: parseFloat(r.multiplyBy) }));
      let priorityFromJson: any[] = [];
      if (f.priorityJson.trim()) {
        try { priorityFromJson = JSON.parse(f.priorityJson); }
        catch { throw new Error('Invalid JSON in priority rules'); }
      }
      const allPriority = [...priorityFromFactors, ...priorityFromJson];
      if (allPriority.length) cm.priority = allPriority;

      // Build speed array from factor rows + raw speedJson (merged)
      const speedFromFactors = f.cmFactors
        .filter(r => r.type === 'speed' && r.condition.trim() && r.multiplyBy.trim())
        .map(r => ({ if: r.condition.trim(), limit_to: parseFloat(r.multiplyBy) }));
      let speedFromJson: any[] = [];
      if (f.speedJson.trim()) {
        try { speedFromJson = JSON.parse(f.speedJson); }
        catch { throw new Error('Invalid JSON in speed rules'); }
      }
      const allSpeed = [...speedFromFactors, ...speedFromJson];
      if (allSpeed.length) cm.speed = allSpeed;

      if (Object.keys(cm).length) body.custom_model = cm;
    }
  }

  if (f.altEnable) {
    body.alternative_routes = {
      target_count: f.altCount || 2,
      share_factor: f.altShare || 0.6,
      weight_factor: f.altWeight || 1.4,
    };
  }
  return body;
}

function parseIndexList(str: string, max: number): number[] | null {
  if (!str.trim()) return null;
  return str.split(',').map(s => parseInt(s.trim(), 10)).filter(n => Number.isInteger(n) && n >= 0 && n < max);
}

export function buildMatrixBody(f: FormState): MatrixBody {
  const locs = coordsFromForm(f);
  if (locs.length < 2) throw new Error('Matrix needs at least 2 locations');

  const body: MatrixBody = { locations: locs, units: f.units };
  if (f.mPreference && f.mPreference !== 'recommended') body.preference = f.mPreference;
  const metrics = Object.entries(f.metrics).filter(([, v]) => v).map(([k]) => k);
  if (metrics.length) body.metrics = metrics;

  const src = parseIndexList(f.mSources, locs.length);
  const dst = parseIndexList(f.mDestinations, locs.length);
  if (src && src.length) body.sources = src;
  if (dst && dst.length) body.destinations = dst;
  if (f.mResolve) body.resolve_locations = true;

  if (f.profile === 'driving-hgv') {
    const restrictions = collectRestrictions(f);
    if (Object.keys(restrictions).length) body.profile_params = { restrictions };
  }
  return body;
}

export function endpointFor(mode: 'directions' | 'matrix', profile: string, format: 'json' | 'geojson' | 'gpx' = 'json'): string {
  return mode === 'matrix' ? `matrix/${profile}` : `directions/${profile}/${format}`;
}

